import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import onClickOutside from 'ember-click-outside/modifiers/on-click-outside';
import moment from 'moment';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sanitize } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceSummaryIndicatorComponent from 'webapp/components/data-performance/summary/indicator';
import type { HtmlEditorOptions } from 'webapp/components/form/html-editor';
import FormHtmlEditorFullComponent from 'webapp/components/form/html-editor-full';

import type BlockSetup from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';
import type VisualizationAiSummary from 'webapp/models-custom/visualization/ai-summary';

interface Args {
	blockState: BlockState;
}

export const enum AiSummaryContentStatus {
	LOADING = -1, //in process of auto generating summary
	GENERATED = 0, //summary generated by ai service
	LIMIT_REACHED = 1, //limit reached
	ERROR = 2, //error occurred
	EDITABLE = 3, //user edited and can be editable on FE
	NO_DATA = 4, //no data available
	METRIC_NOT_SELECTED = 5,
}

export default class BlockContentAiSummaryComponent extends Component<Args> {
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked private userPendingText: string | null = null;
	@tracked isEditing = false;

	private summaryContainerElement: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setupAiSummaryEditingStateChanges();
	}

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization as VisualizationAiSummary;
	}

	get pendingText(): string {
		// if user has made changes, use their pending text
		if (this.userPendingText !== null) {
			return this.userPendingText;
		}

		return this.visualization.shownText || '&nbsp;';
	}

	get trend() {
		return this.block.shownAiSummaryTrend;
	}

	get trendText() {
		return this.mmDataPerformanceSummariesService.getTrendDescription(this.trend);
	}

	get sanitizedText() {
		const text = this.visualization.shownText || '&nbsp;';
		return sanitize(text);
	}

	get hasPendingChanges(): boolean {
		return this.userPendingText !== null && this.userPendingText !== this.sanitizedText;
	}

	// only show revert if editing unedited summary
	get showRevert(): boolean {
		return this.hasPendingChanges && !this.block.aiSummaryIsEdited;
	}

	get editorOptions(): HtmlEditorOptions {
		return {
			toolbar:
				'fontselect styleselect | forecolor bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | emoticons removeformat link',
			isInputFocused: true,
		};
	}

	get showTrend() {
		return this.visualization.showTrend && !this.isEditing;
	}

	get editingOrigin(): string {
		return this.block.isFromReport ? 'editor' : 'designer';
	}

	@action
	setupView(element: HTMLElement) {
		this.summaryContainerElement = element;
	}

	@action
	handleClickOutside(e: Event) {
		const target = e.target as HTMLElement;

		// ignore clicks on elements that should trigger editing or revert
		if (
			target.closest('.ai-edit') ||
			target.closest('.revert-link') ||
			target.closest('.tox-dialog__body')
		) {
			return;
		}

		if (this.summaryContainerElement && !this.summaryContainerElement.contains(target)) {
			if (this.isEditing) {
				this.toggleEditing();
				this.applyChanges();
			}
		}
	}

	@action
	onTextChange(text: string) {
		this.userPendingText = text;
	}

	@action
	revertChanges() {
		this.userPendingText = this.visualization.shownText || '';
		this.toggleEditing();
	}

	@action
	toggleEditing() {
		this.isEditing = !this.isEditing;
		this.eventManagerService.trigger(AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED, {
			blockId: this.block.id,
			isEditing: this.isEditing,
		});
	}

	override willDestroy() {
		super.willDestroy();

		this.eventManagerService.off(AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED, this);
	}

	private setupAiSummaryEditingStateChanges() {
		this.eventManagerService.on(
			AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED,
			this,
			(eventData) => {
				if (eventData.blockId === this.block.id) {
					this.isEditing = eventData.isEditing;
				}
			},
		);
	}

	private applyChanges() {
		if (this.userPendingText && this.hasPendingChanges) {
			const block = this.block;

			block.aiSummaryEditedBy = this.currentUserService.model?.name || null;
			block.aiSummaryEditedOn = moment().unix();
			block.aiSummaryShowTrend = false;
			// text is actually content, which expects text on text prop
			block.text = JSON.stringify({ text: this.userPendingText });
			block.blockSetup.saveBlock();

			this.analyticsService.event(TrackingEvent.AI_SUMMARY_EDITED, {
				origin: this.editingOrigin,
			});
		}
	}
	<template>
		<div class="data-performance-summary block-summary">
			{{#if this.showTrend}}
				<DataPerformanceSummaryIndicatorComponent
					@trend={{this.trend}}
					@text={{this.trendText}}
				/>
			{{/if}}

			<div
				class="summary-container {{if this.isEditing 'summary-container--being-edited'}}"
				{{didInsert this.setupView}}
				{{onClickOutside this.handleClickOutside}}
			>
				{{#if this.isEditing}}
					<div class="bc__html-content">
						<FormHtmlEditorFullComponent
							@text={{this.pendingText}}
							@onChange={{this.onTextChange}}
							@options={{this.editorOptions}}
						/>
					</div>
				{{else}}
					{{{this.sanitizedText}}}
				{{/if}}

				{{#if this.showRevert}}
					<div class="summary-container--warning">
						<span class="warning-text">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />Summary will no
							longer auto-update after making changes.
						</span>
						<a class="revert-link" {{on "click" this.revertChanges}}>Revert</a>
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
